import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Input,
  Select,
  Button,
  Modal,
  Form,
  Popconfirm,
  message,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { ThreeDot } from "react-loading-indicators";

import { Header } from "../components";
import {
  fetchCities,
  createCity,
  updateCity,
  deleteCity,
  fetchDriverCities,
  createDriverCity,
  deleteDriverCity,
  fetchDriverOptions,
} from "../utils/fetchCities";
import { useAuth } from "../contexts/AuthProvider";
import { hasPermission } from "../utils/auth";

// Inline-editable text cell: commits on blur / Enter only when the value changed.
const EditableTextCell = ({ value, placeholder, required, onSave }) => {
  const [val, setVal] = useState(value ?? "");

  useEffect(() => {
    setVal(value ?? "");
  }, [value]);

  const commit = () => {
    const trimmed = (val ?? "").trim();
    if (trimmed === (value ?? "")) return; // nothing changed
    if (required && !trimmed) {
      message.warning("El nombre no puede quedar vacío");
      setVal(value ?? "");
      return;
    }
    onSave(trimmed);
  };

  return (
    <Input
      value={val}
      placeholder={placeholder}
      variant="borderless"
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onPressEnter={(e) => e.target.blur()}
    />
  );
};

const Cities = () => {
  const { user } = useAuth();
  const canWriteCities = hasPermission(user?.permissions, "cities:write");
  const canDeleteCities = hasPermission(user?.permissions, "cities:delete");

  const [cities, setCities] = useState([]);
  const [driverCities, setDriverCities] = useState([]);
  const [drivers, setDrivers] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchText, setSearchText] = useState("");
  const [savingCity, setSavingCity] = useState({}); // { [cityId]: bool }
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  const loadAll = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [citiesData, driverCitiesData, driversData] = await Promise.all([
        fetchCities(),
        fetchDriverCities(),
        fetchDriverOptions(),
      ]);
      setCities(citiesData || []);
      setDriverCities(driverCitiesData || []);
      setDrivers(driversData || []);
    } catch (err) {
      console.log(err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  // Group assignments by city_id for quick lookup while rendering rows.
  const assignmentsByCity = useMemo(() => {
    const map = {};
    (driverCities || []).forEach((a) => {
      if (!map[a.city_id]) map[a.city_id] = [];
      map[a.city_id].push(a);
    });
    return map;
  }, [driverCities]);

  const driverOptions = useMemo(
    () =>
      (drivers || []).map((d) => ({ label: d.name, value: d.personnel_id })),
    [drivers]
  );

  const filteredCities = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return cities;
    return cities.filter((c) =>
      [c.name, c.region, c.country]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [cities, searchText]);

  const apiError = (err, fallback) =>
    message.error(err?.response?.data?.message || fallback);

  // ----- City field edits -----
  const handleUpdateCity = async (id, field, value) => {
    try {
      await updateCity(id, { [field]: value });
      setCities((prev) =>
        prev.map((c) => (c.id === id ? { ...c, [field]: value } : c))
      );
      message.success("Ciudad actualizada");
    } catch (err) {
      apiError(err, "No se pudo actualizar la ciudad");
    }
  };

  const handleDeleteCity = async (id) => {
    try {
      await deleteCity(id);
      setCities((prev) => prev.filter((c) => c.id !== id));
      message.success("Ciudad eliminada");
    } catch (err) {
      apiError(err, "No se pudo eliminar la ciudad");
    }
  };

  // ----- Driver assignment (a city has at most one driver) -----
  const handleDriverChange = async (cityId, personnelId) => {
    const current = assignmentsByCity[cityId] || [];
    // No change if the same driver is picked again.
    if (current.length === 1 && current[0].personnel_id === personnelId) return;

    setSavingCity((prev) => ({ ...prev, [cityId]: true }));
    try {
      // Clear any existing assignment, then set the new one (if any).
      await Promise.all(current.map((a) => deleteDriverCity(a.id)));
      if (personnelId != null) {
        await createDriverCity({
          personnel_id: Number(personnelId),
          city_id: Number(cityId),
        });
      }
      // Refetch so we resync ids of newly created links.
      const fresh = await fetchDriverCities();
      setDriverCities(fresh || []);
      message.success("Chofer actualizado");
    } catch (err) {
      apiError(err, "No se pudo actualizar la asignación");
    } finally {
      setSavingCity((prev) => ({ ...prev, [cityId]: false }));
    }
  };

  // ----- Create city -----
  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      setCreating(true);
      const payload = {
        name: values.name.trim(),
        region: values.region?.trim() || undefined,
        country: values.country?.trim() || undefined,
      };
      const created = await createCity(payload);
      // Fall back to the submitted payload if the API doesn't echo the row.
      setCities((prev) => [...prev, created?.id ? created : { id: created, ...payload }]);
      message.success("Ciudad creada");
      setCreateOpen(false);
      form.resetFields();
      // Reload to guarantee ordering/ids match the backend.
      loadAll();
    } catch (err) {
      if (err?.errorFields) return; // validation error, already shown inline
      apiError(err, "No se pudo crear la ciudad");
    } finally {
      setCreating(false);
    }
  };

  const columns = [
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      defaultSortOrder: "ascend",
      render: (_, record) =>
        canWriteCities ? (
          <EditableTextCell
            value={record.name}
            placeholder="Nombre"
            required
            onSave={(v) => handleUpdateCity(record.id, "name", v)}
          />
        ) : (
          record.name
        ),
    },
    {
      title: "Región",
      dataIndex: "region",
      key: "region",
      render: (_, record) =>
        canWriteCities ? (
          <EditableTextCell
            value={record.region}
            placeholder="—"
            onSave={(v) => handleUpdateCity(record.id, "region", v)}
          />
        ) : (
          record.region || "—"
        ),
    },
    {
      title: "País",
      dataIndex: "country",
      key: "country",
      render: (_, record) =>
        canWriteCities ? (
          <EditableTextCell
            value={record.country}
            placeholder="—"
            onSave={(v) => handleUpdateCity(record.id, "country", v)}
          />
        ) : (
          record.country || "—"
        ),
    },
    {
      title: "Chofer asignado",
      key: "driver",
      width: "30%",
      render: (_, record) => {
        const assigned = assignmentsByCity[record.id] || [];
        return (
          <Select
            showSearch
            allowClear
            style={{ width: "100%" }}
            placeholder="Asignar chofer"
            options={driverOptions}
            value={assigned[0]?.personnel_id ?? undefined}
            loading={!!savingCity[record.id]}
            disabled={!canWriteCities || !!savingCity[record.id]}
            optionFilterProp="label"
            onChange={(id) => handleDriverChange(record.id, id ?? null)}
          />
        );
      },
    },
    ...(canDeleteCities
      ? [
          {
            title: "",
            key: "actions",
            width: 60,
            align: "center",
            render: (_, record) => (
              <Popconfirm
                title="¿Eliminar esta ciudad?"
                okText="Eliminar"
                cancelText="Cancelar"
                okButtonProps={{ danger: true }}
                onConfirm={() => handleDeleteCity(record.id)}
              >
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="bg-white dark:bg-main-dark-bg rounded-lg">
      <div className="mb-4 flex justify-between items-center">
        <Header category="Página" title="Ciudades" />
        {canWriteCities && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateOpen(true)}
          >
            Nueva ciudad
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="w-full text-center min-h-72 flex items-center justify-center">
          <ThreeDot variant="brick-stack" color="#4048F1" size="medium" text="" textColor="" />
        </div>
      ) : error ? (
        <div>No se pudieron cargar las ciudades.</div>
      ) : (
        <>
          <div className="mb-4" style={{ maxWidth: 320 }}>
            <Input
              allowClear
              placeholder="Buscar ciudad..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={filteredCities}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
            }}
            onChange={(p) =>
              setPagination({ current: p.current, pageSize: p.pageSize })
            }
          />
        </>
      )}

      <Modal
        title="Nueva ciudad"
        open={createOpen}
        onOk={handleCreate}
        onCancel={() => {
          setCreateOpen(false);
          form.resetFields();
        }}
        confirmLoading={creating}
        okText="Crear"
        cancelText="Cancelar"
      >
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            name="name"
            label="Nombre"
            rules={[{ required: true, message: "El nombre es obligatorio" }]}
          >
            <Input placeholder="Ej. Santiago" />
          </Form.Item>
          <Form.Item name="region" label="Región">
            <Input placeholder="Ej. Metropolitana" />
          </Form.Item>
          <Form.Item name="country" label="País">
            <Input placeholder="Ej. Chile" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Cities;
